import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { SupportStreamService } from './support-stream.service';

/** What woke the consumer up. Consumers refetch either way; the distinction exists
 *  so a test (and a future observability hook) can tell the instant path from the
 *  floor. */
export type RefreshTrigger = 'frame' | 'poll';

/** Tight cadence while the agent is actually looking at the console. */
export const LIVE_POLL_VISIBLE_MS = 3000;
/** Relaxed cadence while the tab is hidden — nobody is watching, so don't burn
 *  requests; the title-flash notification (SupportNotificationService) is what
 *  brings the agent back. */
export const LIVE_POLL_HIDDEN_MS = 15000;

/**
 * "Something may have changed — go refetch" for the agent console (AB#1866).
 *
 * WHY A POLL AT ALL. The server's live push is real but feature-gated OFF in every
 * environment today: `iac/support/main.bicep` defaults `topicEventsEnabled` to false
 * (and `main.prod.bicepparam` sets it false too), so
 * `main.py::_run_wake_consumer_if_enabled` returns immediately and the Service Bus
 * wake relay never runs. An SSE connection therefore delivers its catch-up replay on
 * connect and then emits nothing but 15s keepalives — forever. Subscribing alone
 * would look live and refresh nothing.
 *
 * So this merges the two: the SSE frame is the INSTANT path (and lights up for free
 * the day `topicEventsEnabled` is turned on), and the interval is the RELIABLE FLOOR
 * that makes the console correct today regardless. This is deliberately the same
 * shape — and the same two cadences — as the customer widget's
 * `SupportSessionService` in bluetitan-assistant, which solved this first; keeping
 * one shape means AB#1821 (Azure Web PubSub, retire SSE + poll) has one pattern to
 * retire on both surfaces rather than two.
 *
 * AND THE POLL DOES NOT GO AWAY WHEN THE FLAG FLIPS — at least not for the inbox.
 * An agent-wide connection's scope is resolved ONCE, at connect time:
 * `routers/stream.py::_catchup_for_principal` freezes `list_streamable_ticket_ids`
 * into a `frozenset`, and `LiveConnectionRegistry.connections_for_ticket` matches a
 * `ticket_id is None` connection only when the woken ticket is already IN that frozen
 * set. A ticket created after the agent connected is not, so a brand-new ticket — the
 * inbox's single most important event — can never reach an agent-wide tail by wake
 * alone. Turning `topicEventsEnabled` on makes replies to KNOWN tickets instant; the
 * interval remains the only thing that surfaces a NEW one. Fixing that properly means
 * re-resolving the scope on the server (or a tenant-level wake topic), which is
 * AB#1821's territory, not this service's.
 *
 * Emits a signal only. It never carries payload and never fetches: every consumer
 * re-reads through its own authorized API call, so nothing here can widen what an
 * agent is allowed to see.
 */
@Injectable({ providedIn: 'root' })
export class LiveRefreshService {
  private stream = inject(SupportStreamService);
  private doc = inject(DOCUMENT);

  /**
   * @param scope `null` for the agent-wide stream (every ticket the principal can
   *   read — what the inbox wants), or a ticket id to narrow to one thread.
   * @param opts.stream open the SSE connection as well as the interval. Defaults to
   *   `true`. Pass `false` for a principal the stream cannot AUTHENTICATE, so the
   *   caller still gets the reliable interval floor without a permanent retry loop.
   *
   *   This is split out rather than folded into `scope` because the two halves have
   *   different auth paths, and only one of them is universal. The interval drives
   *   the consumer's own `SupportApiService` call, which goes through
   *   `auth.interceptor.ts` and therefore works on BOTH the workforce and partner
   *   rails. `SupportStreamService.connect` acquires its token solely from
   *   `MsalService` (never `CiamAuthService`), so on the partner rail it sends no
   *   Authorization header at all, `routers/stream.py::_resolve_principal` 401s it,
   *   and the 2s reconnect backoff then retries for the life of the tab —
   *   ~1800 rejected requests/hour/tab, delivering nothing. Skipping the stream for
   *   such a principal costs no refresh (the interval is what actually refreshes
   *   today — see WHY A POLL AT ALL above) and removes the loop.
   *
   *   FOLLOW-UP, deliberately not done here: giving `SupportStreamService` a CIAM
   *   token path would let the partner rail have the instant path too. That is a
   *   change to the stream service's auth, not to this refresh contract.
   *   (security-check MEDIUM, PR #1571.)
   */
  watch(scope: string | null, opts?: { stream?: boolean }): Observable<RefreshTrigger> {
    const wantStream = opts?.stream ?? true;
    return new Observable<RefreshTrigger>((subscriber) => {
      let handle: ReturnType<typeof setInterval> | undefined;

      const schedule = (): void => {
        if (handle !== undefined) clearInterval(handle);
        const hidden = this.doc.visibilityState === 'hidden';
        handle = setInterval(
          () => subscriber.next('poll'),
          hidden ? LIVE_POLL_HIDDEN_MS : LIVE_POLL_VISIBLE_MS,
        );
      };

      // Re-schedule (not just re-read) on every visibility flip, so the cadence
      // tightens the moment the agent comes back rather than at the end of a
      // 15s hidden interval already in flight.
      //
      // AND emit immediately on BECOMING VISIBLE. Re-scheduling discards the elapsed
      // time on the pending interval, so without this the agent who just looked back at
      // the console waits a further full 3s while a stale list sits on screen — and an
      // agent alternating sub-3s glances with sub-15s absences would never poll at all,
      // which is precisely the bug this service exists to fix. Returning to the tab is
      // the moment fresh data matters most; it should not be the moment the timer resets.
      // (code-review, PR #1571.)
      const onVisibilityChange = (): void => {
        const becameVisible = this.doc.visibilityState !== 'hidden';
        schedule();
        if (becameVisible) subscriber.next('poll');
      };
      this.doc.addEventListener('visibilitychange', onVisibilityChange);
      schedule();

      const streamSub = wantStream
        ? this.stream.connect(scope).subscribe(() => subscriber.next('frame'))
        : null;

      return () => {
        if (handle !== undefined) clearInterval(handle);
        this.doc.removeEventListener('visibilitychange', onVisibilityChange);
        streamSub?.unsubscribe();
      };
    });
  }
}
